import { SEO } from "@/components/SEO";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LocalizedLink } from "@/components/LocalizedLink";
import { Crown, Shield, Award, TrendingUp, Phone } from "lucide-react";

const UK = () => {
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "European Markets", url: "/european-markets" },
    { name: "United Kingdom", url: "/markets/uk" }
  ];

  return (
    <>
      <SEO
        title="Luxury Smart Toilets & Bathtubs UK Supplier | Aureum VII"
        description="UK trade supply of smart toilets, freestanding bathtubs and whirlpool spas for developers, hotels and architects. CE certified, EU logistics."
        keywords="intelligent toilet UK, smart WC UK, intelligent loo, luxury smart toilet UK, premium WC Britain, British bathroom technology, smart lavatory UK"
        breadcrumbs={breadcrumbs}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Intelligent Toilet UK",
          "description": "Premium intelligent toilet systems for the UK market",
          "url": "https://www.aureum7trading.com/markets/uk"
        }}
      />
      <Navigation />
      
      <main className="min-h-screen bg-gradient-to-b from-background to-background/95">
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
                  <span className="text-2xl">🇬🇧</span>
                  <span className="text-sm font-medium">British Quality</span>
                </div>
                
                <h1 className="text-5xl md:text-6xl font-bold mb-6">
                  Intelligent Toilet UK
                </h1>
                
                <p className="text-xl text-muted-foreground mb-8">
                  Where British heritage meets intelligent innovation. Premium smart WC systems 
                  delivering exceptional quality, reliable service, and timeless sophistication.
                </p>

                <div className="flex items-center gap-2 px-6 py-3 bg-card rounded-lg border mb-8 inline-flex">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  <span className="font-semibold">3,000-5,000</span>
                  <span className="text-muted-foreground">monthly searches</span>
                </div>

                <div className="flex flex-wrap gap-4">
                  <Button asChild size="lg">
                    <LocalizedLink to="/contact">Request Quote</LocalizedLink>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <LocalizedLink to="/smart-toilets">View Collection</LocalizedLink>
                  </Button>
                </div>
              </div>

              <div className="relative">
                <Card className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <Crown className="w-8 h-8 text-primary" />
                    <h3 className="text-2xl font-bold">British Standards</h3>
                  </div>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">BS EN Standards Certified</p>
                        <p className="text-sm text-muted-foreground">Full compliance with UK building regulations</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Award className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Premium Quality Assurance</p>
                        <p className="text-sm text-muted-foreground">Rigorous testing to British quality standards</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Phone className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">UK-Based Support</p>
                        <p className="text-sm text-muted-foreground">Dedicated customer service team in Britain</p>
                      </div>
                    </li>
                  </ul>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Quality Section */}
        <section className="py-20 px-4 bg-card/30">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Heritage of Excellence
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="p-6">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <Crown className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Premium Quality</h3>
                <p className="text-muted-foreground">
                  British standards of excellence in every component, from materials to manufacturing processes
                </p>
              </Card>

              <Card className="p-6">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <Shield className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Reliable Service</h3>
                <p className="text-muted-foreground">
                  Comprehensive after-sales support with UK-based technical expertise and rapid response times
                </p>
              </Card>

              <Card className="p-6">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <Award className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Timeless Design</h3>
                <p className="text-muted-foreground">
                  Elegant aesthetics that complement traditional and contemporary British interiors
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Why Choose UK Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold text-center mb-12">
              Why Choose Our Intelligent Toilet for the UK?
            </h2>
            
            <div className="space-y-8">
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-3">British Building Regulations Compliance</h3>
                <p className="text-muted-foreground">
                  Every intelligent WC system is designed to meet or exceed UK building regulations, 
                  including Part G (Sanitation), Part L (Conservation of fuel and power), and Part H (Drainage).
                </p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-3">Heritage Quality Standards</h3>
                <p className="text-muted-foreground">
                  Our commitment to quality reflects British craftsmanship traditions. Each smart loo 
                  undergoes rigorous testing to ensure it meets the high standards expected by UK customers.
                </p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-3">Comprehensive Service Network</h3>
                <p className="text-muted-foreground">
                  UK-wide installation and maintenance support with trained technicians who understand 
                  British plumbing systems and customer service expectations.
                </p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-3">Water Efficiency for UK Homes</h3>
                <p className="text-muted-foreground">
                  Designed with UK water conservation in mind, our intelligent toilets significantly 
                  reduce water consumption while maintaining superior flushing performance.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Experience British Quality with Intelligent Innovation
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Speak with our UK team about premium smart WC solutions for your property
            </p>
            <Button asChild size="lg">
              <LocalizedLink to="/contact">Contact UK Team</LocalizedLink>
            </Button>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default UK;